/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {
  type FlowRunLogs,
  type IcrsPerProject,
  type ListResultsQuery,
  type MetStation,
  type ObjectIdMap,
  type PlantConfiguration,
  type PowerAlphaRequest,
  type PrevRunRequests,
  type PricingDashboardQueryOptions,
  type PricingDashboardResponse,
  type ProjectBodSnapshot,
  type ProjectCoordinatesEntry,
  type ProjectListEntry,
  type ProjectListExtendedEntry,
  type ProjectObject,
  type ProjectRecord,
  type ProjectResultsSearchParams,
  type ProjectResultsSummaryResponseEntry,
  type ProjectRunPoint,
  type ProjectTeammember,
  type ProjectWeatherFilesResponse,
  type PVSimulationRequest,
  type RemoveBodRequest,
  SalesforceObject,
  type ScenarioRecord,
  type ScenarioStage,
  type SiteListRecord,
  type SiteRecordType,
  type Task,
  type UpdateBodRequest,
  type UserLookup,
} from '@poweralpha/common';

import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { hoursToMilliseconds, minutesToMilliseconds } from 'date-fns';
import type { HTTPError, SearchParamsOption } from 'ky';
import { useMemo } from 'react';
import { api } from '../api';
import { getbnCRMObjectIdMap } from '../api/bncrm';
import type { BatchRequestParams } from '../project/batch/models';
import { objectToQueryKey, queryClient } from '../queryClient';
import type { MutationOptions, QueryOptions } from '../react-components/queries';
import { salesforceToast } from '../react-components/salesforce/SalesforceToast';

const getScenarioQueryKey = (scenarioId: string) => ['scenario-data', scenarioId];

export const useScenarioQuery = (
  projectId: string,
  scenarioId: string,
  opts?: QueryOptions<ScenarioRecord>,
) =>
  useQuery({
    queryKey: getScenarioQueryKey(scenarioId),
    queryFn: () => api.get(`projects/${projectId}/scenarios/${scenarioId}`).json<ScenarioRecord>(),
    ...opts,
    staleTime: Number.POSITIVE_INFINITY,
  });

const getScenarioBodQueryKey = (scenarioId: string) => ['scenario-bod', scenarioId];

export const useProjectBodsQuery = (projectId: string | null, enabled = true) =>
  useQuery({
    queryKey: ['project-bods', projectId],
    queryFn: () => api.get(`projects/${projectId}/bod`).json<ScenarioStage[]>(),
    enabled: Boolean(projectId) && enabled,
    staleTime: 60e3,
  });

const bodSnapshotQueryKey = ['bod-snapshot'] as const;

/** The project's newest runs that have a capex and a yr-1 energy, for the runs plot. */
export const useProjectRunPointsQuery = (projectId: string) =>
  useQuery({
    queryKey: ['project-run-points', projectId],
    queryFn: () => api.get(`projects/${projectId}/run-points`).json<Array<ProjectRunPoint>>(),
    staleTime: minutesToMilliseconds(2),
  });

/**
 * Latest basis of design and plan of record, with headline metrics, per project in the user's
 * regions: only their team's projects when `starred`, or one project when `projectId` is set.
 */
export const useBodSnapshotQuery = (
  apiOptions: { starred?: boolean; projectId?: string },
  opts?: QueryOptions<Array<ProjectBodSnapshot>>,
) =>
  useQuery({
    queryKey: [...bodSnapshotQueryKey, ...objectToQueryKey(apiOptions)],
    queryFn: () =>
      api
        .get('results/bod-snapshot', { searchParams: apiOptions as SearchParamsOption })
        .json<Array<ProjectBodSnapshot>>(),
    staleTime: minutesToMilliseconds(5),
    ...opts,
  });

export const useScenarioBodQuery = (
  projectId: string | null,
  scenarioId: string,
  opts?: QueryOptions<ScenarioStage | null>,
) =>
  useQuery({
    queryKey: getScenarioBodQueryKey(scenarioId),
    queryFn: () => api.get(`projects/${projectId}/bod/${scenarioId}`).json<ScenarioStage | null>(),
    enabled: Boolean(projectId && scenarioId) && (opts?.enabled ?? true),
    staleTime: 60e3, // 1min
    ...opts,
  });

export const useRemoveScenarioBodQuery = (
  projectId: string | null,
  opts?: MutationOptions<void, HTTPError, RemoveBodRequest>,
) =>
  useMutation({
    mutationFn: async (data) => {
      await api.post(`projects/${projectId}/bod/remove`, { json: data });
      await queryClient.invalidateQueries({ queryKey: ['project-bods', projectId] });
      await queryClient.invalidateQueries({ queryKey: bodSnapshotQueryKey });
    },
    ...opts,
  });

export const useUpdateBodMutation = (projectId: string, scenarioId: string) =>
  useMutation<void, HTTPError, UpdateBodRequest>({
    mutationFn: async (data) => {
      await api.post(`projects/${projectId}/scenarios/${scenarioId}/update-bod`, { json: data });
    },
    onSuccess: () => {
      salesforceToast('BOD updated successfully', projectId, SalesforceObject.project, {
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: getScenarioQueryKey(scenarioId) });
      queryClient.invalidateQueries({ queryKey: getScenarioBodQueryKey(scenarioId) });
      queryClient.invalidateQueries({ queryKey: ['project-bods', projectId] });
      queryClient.invalidateQueries({ queryKey: bodSnapshotQueryKey });
    },
  });

export const getProjectSimulationLogs = async (projectId: string): Promise<FlowRunLogs[]> =>
  api.get(`projects/${projectId}/logs`).json();

export const getProjectResultsSummary = async (
  projectId: string | undefined,
  search?: ProjectResultsSearchParams,
): Promise<ListResultsQuery<ProjectResultsSummaryResponseEntry>> =>
  api
    .get(`projects/${projectId}/results-summary`, {
      searchParams: search as SearchParamsOption,
      timeout: 30e3,
    })
    .json();

export const getProjectsList = async (
  name: string | null = null,
  includeInactiveProjects = false,
  sortBy: 'alphabetical' | 'lastModifiedAt' = 'alphabetical',
  starred = false,
  limit = 100,
  offset = 0,
): Promise<ProjectListEntry[]> => {
  const searchParams: SearchParamsOption = {
    includeInactiveProjects,
    sortBy,
    starred,
    limit,
    offset,
    ...(name ? { name } : {}),
  };
  return api.get('projects', { searchParams }).json();
};

export const getMultipleProjects = async (
  ids: Array<string>,
): Promise<Array<ProjectListExtendedEntry>> =>
  api.get('projects/multiple', { searchParams: { ids: ids.join(',') } }).json();

export const useMultipleProjectsQuery = (
  projectIds: Array<string>,
  opts?: QueryOptions<Array<ProjectListExtendedEntry>>,
) =>
  useQuery({
    queryKey: ['projects-query', ...projectIds],
    queryFn: () =>
      api
        .get('projects/multiple', { searchParams: { ids: projectIds.join(',') } })
        .json<Array<ProjectListExtendedEntry>>(),
    staleTime: 60e3,
    ...opts,
    enabled: Boolean(projectIds.length) && (opts?.enabled ?? true),
  });

export const getProjectMeta = async (
  projectId: string | null | undefined,
): Promise<ProjectRecord> => api.get(`projects/${projectId}`).json();

export const getProjectLeads = async (
  projectId: string | null,
  object: ProjectObject,
): Promise<Record<ProjectTeammember, string | null>> =>
  api.get(`projects/${projectId}/leads`, { searchParams: { object } }).json();

export const getProjectScenarioComparison = async (
  projectId: string,
  scenarioIds: string[],
): Promise<ScenarioRecord[]> =>
  api
    .get(`projects/${projectId}/scenarios/compare?scenario=${scenarioIds.join('&scenario=')}`)
    .json();

export interface GetSitesOptions {
  effectiveOnly?: boolean;
  types?: Array<SiteRecordType>;
}

export function useSitesQuery<T = Array<SiteListRecord>>(
  projectId: string | undefined | null,
  opts?: QueryOptions<Array<SiteListRecord>, HTTPError, T> & { apiOptions?: GetSitesOptions },
) {
  return useQuery({
    queryKey: ['sites', projectId, ...objectToQueryKey(opts?.apiOptions)],
    queryFn: () => {
      const queryParams = [
        `effectiveOnly=${opts?.apiOptions?.effectiveOnly ?? false}`,
        ...(opts?.apiOptions?.types ? [`&type=${opts.apiOptions.types.join('&type=')}`] : []),
      ];
      return api
        .get(`projects/${projectId}/sites?${queryParams.join('&')}`)
        .json<Array<SiteListRecord>>();
    },
    ...opts,
    staleTime: minutesToMilliseconds(1), // 1min
    enabled: projectId ? opts?.enabled : false,
  });
}

export const useProjectQuery = (
  id: string | undefined | null,
  opts?: QueryOptions<ProjectRecord>,
) =>
  useQuery<ProjectRecord>({
    queryKey: ['project-meta', id],
    queryFn: () => getProjectMeta(id),
    staleTime: minutesToMilliseconds(30), // 30 minutes
    ...opts,
    enabled: Boolean(id) && (opts?.enabled ?? true),
  });

export const useSfIdMapQuery = (
  object: SalesforceObject = SalesforceObject.project,
  ids?: Array<string>,
  enabled?: boolean,
) =>
  useQuery<ObjectIdMap>({
    queryKey: [`${object}-id-map`, ...(ids ?? [])],
    queryFn: () => getbnCRMObjectIdMap(object, ids),
    staleTime: minutesToMilliseconds(60 * 24), // 24 hrs
    enabled: ids?.length !== 0 && (enabled ?? true),
  });

/**
 * Resolves id -> name for a mix of standard and M&A project ids, for callers that only have bare
 * ids and cannot tell the two apart. Queries both sObjects and merges; the one that does not own a
 * given id simply omits it.
 */
export const useProjectIdNameMapQuery = (projectIds?: Array<string>, enabled?: boolean) => {
  // `undefined` asks for the full map of both objects, matching `useSfIdMapQuery`.
  const uniqueProjectIds = useMemo(
    () => (projectIds ? [...new Set(projectIds.filter(Boolean))] : undefined),
    [projectIds],
  );

  const standardProjectIdMapQuery = useSfIdMapQuery(
    SalesforceObject.project,
    uniqueProjectIds,
    enabled,
  );
  const maProjectIdMapQuery = useSfIdMapQuery(
    SalesforceObject.ma_project,
    uniqueProjectIds,
    enabled,
  );

  const data = useMemo<ObjectIdMap>(
    () => ({ ...(standardProjectIdMapQuery.data ?? {}), ...(maProjectIdMapQuery.data ?? {}) }),
    [standardProjectIdMapQuery.data, maProjectIdMapQuery.data],
  );

  // Which ids belong to M&A projects, for rows that carry no `project_object` of their own.
  const maProjectIds = useMemo(
    () => new Set(Object.keys(maProjectIdMapQuery.data ?? {})),
    [maProjectIdMapQuery.data],
  );

  return {
    data,
    maProjectIds,
    isLoading: standardProjectIdMapQuery.isLoading || maProjectIdMapQuery.isLoading,
  };
};

export const useProjectsListQuery = (
  opts?: QueryOptions<ListResultsQuery<ProjectListEntry>> & {
    apiOptions?: {
      includeInactiveProjects?: boolean;
      name?: string;
      sortBy?: 'alphabetical' | 'lastModifiedAt';
      starred?: boolean;
      includeMAProjects?: boolean;
      limit?: number;
      offset?: number;
    };
  },
) =>
  useQuery({
    queryKey: ['all-projects', ...objectToQueryKey(opts?.apiOptions)],
    queryFn: () => {
      const searchParams: SearchParamsOption = {
        includeInactiveProjects: opts?.apiOptions?.includeInactiveProjects,
        sortBy: opts?.apiOptions?.sortBy,
        starred: opts?.apiOptions?.starred,
        includeMAProjects: opts?.apiOptions?.includeMAProjects,
        limit: opts?.apiOptions?.limit,
        offset: opts?.apiOptions?.offset,
        ...(opts?.apiOptions?.name ? { name: opts.apiOptions.name } : {}),
      };
      return api.get('projects', { searchParams }).json<ListResultsQuery<ProjectListEntry>>();
    },
    enabled: opts?.enabled ?? true,
    staleTime: minutesToMilliseconds(60), // 1hr
    ...opts,
  });

export const useProjectsCoordinatesQuery = (
  opts?: QueryOptions<ProjectCoordinatesEntry[]> & {
    apiOptions?: {
      includeInactiveProjects?: boolean;
      name?: string;
      starred?: boolean;
      includeMAProjects?: boolean;
    };
  },
) =>
  useQuery<ProjectCoordinatesEntry[]>({
    queryKey: ['projects-coordinates', ...objectToQueryKey(opts?.apiOptions)],
    queryFn: () => {
      const searchParams: SearchParamsOption = {
        includeInactiveProjects: opts?.apiOptions?.includeInactiveProjects,
        starred: opts?.apiOptions?.starred,
        includeMAProjects: opts?.apiOptions?.includeMAProjects,
        ...(opts?.apiOptions?.name ? { name: opts.apiOptions.name } : {}),
      };
      return api.get('projects/coordinates', { searchParams }).json<ProjectCoordinatesEntry[]>();
    },
    placeholderData: keepPreviousData,
    enabled: opts?.enabled ?? true,
    staleTime: minutesToMilliseconds(60), // 1hr
    ...opts,
  });

export const useResultsSummaryQuery = <T = ListResultsQuery<ProjectResultsSummaryResponseEntry>>(
  projectId: string | undefined,
  opts?: QueryOptions<ListResultsQuery<ProjectResultsSummaryResponseEntry>, HTTPError, T> & {
    apiOptions?: ProjectResultsSearchParams;
  },
) =>
  useQuery({
    queryKey: ['project-results-summary', projectId, ...objectToQueryKey(opts?.apiOptions)],
    queryFn: () =>
      api
        .get(`projects/${projectId}/results-summary`, {
          searchParams: opts?.apiOptions as SearchParamsOption,
          timeout: 30e3,
        })
        .json<ListResultsQuery<ProjectResultsSummaryResponseEntry>>(),
    ...opts,
  });

export interface GetProjectWeatherFilesOptions {
  limit?: number;
  offset?: number;
  radius?: number;
  showArchived?: boolean;
  hasRainfall?: boolean;
  bod?: boolean;
}

export const useProjectWeatherFilesQuery = <T = ProjectWeatherFilesResponse>(
  projectId: string | undefined,
  opts?: QueryOptions<ProjectWeatherFilesResponse, HTTPError, T> & {
    apiOptions?: GetProjectWeatherFilesOptions;
  },
) =>
  useQuery({
    queryKey: ['project-weather-files', projectId, ...objectToQueryKey(opts?.apiOptions)],
    queryFn: () =>
      api
        .get(`projects/${projectId}/weather-files`, {
          searchParams: opts?.apiOptions as SearchParamsOption,
        })
        .json<ProjectWeatherFilesResponse>(),
    staleTime: minutesToMilliseconds(1),
    ...opts,
    enabled: Boolean(projectId) && (opts?.enabled ?? true),
  });

type ProjectPricingDashboardQueryOptions = Omit<PricingDashboardQueryOptions, 'project_id'>;

export const getProjectPricingData = async (
  projectId: string,
  opts?: ProjectPricingDashboardQueryOptions,
): Promise<PricingDashboardResponse> => {
  const searchParams: SearchParamsOption = {
    ...(opts?.search ? { search: opts.search } : {}),
    limit: opts?.limit ?? 10,
    offset: opts?.offset ?? 0,
    ...(opts?.plant_type ? { plant_type: opts.plant_type } : {}),
    ...(opts?.transaction ? { transaction: opts.transaction } : {}),
    ...(opts?.status ? { status: opts.status } : {}),
    ...(opts?.region ? { region: opts.region } : {}),
    ...(opts?.sort_by ? { sort_by: opts.sort_by } : {}),
    ...(opts?.sort_direction ? { sort_direction: opts.sort_direction } : {}),
    ...(opts?.updated_at ? { updated_at: opts.updated_at } : {}),
  };
  return api.get(`pricing-dashboard/${projectId}`, { searchParams }).json();
};

export const useProjectPricingDataQuery = <T = PricingDashboardResponse>(
  projectId: string | null | undefined,
  opts?: QueryOptions<PricingDashboardResponse, HTTPError, T> & {
    apiOptions?: ProjectPricingDashboardQueryOptions;
  },
) =>
  useQuery({
    queryKey: ['project-pricing-dashboard', projectId, ...objectToQueryKey(opts?.apiOptions)],
    queryFn: () => getProjectPricingData(projectId ?? '', opts?.apiOptions),
    staleTime: minutesToMilliseconds(5),
    ...opts,
    enabled: Boolean(projectId) && (opts?.enabled ?? true),
  });

export const useProjectSimulateMutation = (
  opts?: MutationOptions<
    Task,
    HTTPError,
    {
      request: PowerAlphaRequest;
      plantConfiguration: Array<PlantConfiguration>;
    }
  >,
) =>
  useMutation({
    mutationFn: async ({ request, plantConfiguration }) =>
      api
        .post(`simulate?plant_configuration=${plantConfiguration.join('+')}`, {
          json: { paRequest: request },
        })
        .json(),
    ...opts,
  });

export const usePrevRunRequestsQuery = (scenarioId: string | undefined) =>
  useQuery<PrevRunRequests>({
    queryKey: ['prev-run-requests', scenarioId],
    queryFn: () => api.get(`results/${scenarioId}/configs`).json(),
    enabled: Boolean(scenarioId),
  });

export interface BatchCountResponse {
  count: number | null;
}

type pvBatchParams = Pick<BatchRequestParams, 'gcr' | 'dc_ac_ratio' | 'ilr'>;

export const useFetchPVBatchCounts = (
  opts?: MutationOptions<BatchCountResponse, HTTPError, pvBatchParams>,
) =>
  useMutation({
    mutationFn: (data) => {
      return api
        .post('proxy/pa/batch/pv-counter', {
          json: { ...data },
        })
        .json();
    },
    ...opts,
  });

export const useFetchCapBankPVBatchCounts = (
  opts?: MutationOptions<BatchCountResponse, HTTPError, PVSimulationRequest>,
) =>
  useMutation({
    mutationFn: (request) => {
      return api
        .post('proxy/pa/batch/pv-pf-counter', {
          json: { ...request },
        })
        .json();
    },
    ...opts,
  });

type essBatchParams = Pick<BatchRequestParams, 'ess_mw' | 'ess_augmentation_count'>;

type EssBatchVariables = {
  data: essBatchParams;
  contracted_term: number;
};

export const useFetchESSBatchCounts = (
  opts?: MutationOptions<BatchCountResponse, HTTPError, EssBatchVariables>,
) =>
  useMutation({
    mutationFn: ({ data, contracted_term }) => {
      return api
        .post('proxy/pa/batch/ess-counter', {
          json: { ...data, ess_ac_power_mw: data.ess_mw, contracted_term },
        })
        .json();
    },
    ...opts,
  });

export const useUserQuery = (
  opts?: QueryOptions<Array<UserLookup>> & { apiOptions?: { isActive?: boolean } },
) =>
  useQuery<Array<UserLookup>>({
    queryKey: ['userQuery', ...objectToQueryKey(opts?.apiOptions)],
    queryFn: () =>
      api
        .get('bncrm/users', { searchParams: { isActive: opts?.apiOptions?.isActive ?? true } })
        .json(),
    staleTime: hoursToMilliseconds(24),
    ...opts,
  });

export const useIcrsPerProjectQuery = (
  projectIds: Array<string> | undefined,
  opts?: QueryOptions<IcrsPerProject>,
) =>
  useQuery<IcrsPerProject>({
    queryKey: ['icrs-per-project', ...(projectIds ?? [])],
    queryFn: () => api.get(`projects/icrs?id=${projectIds?.join('&id=')}`).json(),
    ...opts,
    staleTime: minutesToMilliseconds(5),
    enabled: Boolean(projectIds?.length) && (opts?.enabled ?? true),
  });

export const useMAApprovalStagesQuery = (opts?: QueryOptions<Array<string>>) =>
  useQuery<Array<string>>({
    queryKey: ['ma-approval-stages'],
    queryFn: () => api.get('projects/ma/approval-stages').json<Array<string>>(),
    staleTime: hoursToMilliseconds(24),
    ...opts,
  });

export const useLatestMetStationQuery = (projectId: string | undefined) =>
  useQuery<MetStation | null, HTTPError>({
    queryKey: ['project-met-station', projectId],
    queryFn: () => api.get(`projects/${projectId}/met-stations/latest`).json<MetStation>(),
    enabled: Boolean(projectId),
    staleTime: minutesToMilliseconds(5),
  });
